import { List } from 'immutable'
import { createSelector } from 'reselect'
import { getOr, getInOr, safeToJS, flattenList, get } from 'utils/immutable'
import { equals, prop, path } from 'ramda'
import { isFalse } from 'utils/types'
import { availableCircuitsByRP, availableCircuits } from 'selectors/ctp'

const ctpMapping = (state) => state.get('ctpMappingWorkflow')

export const getCtpMappingWorkflow = createSelector(ctpMapping, (state) =>
  state.toJS()
)

const selectSelectedRPs = (state) =>
  state.getIn(['ctpMappingWorkflow', 'selectedRPs'])
const selectCtpAssignments = (state) =>
  state.getIn(['ctpMappingWorkflow', 'assignments'])
const selectCtpStagedAssignments = (state) =>
  state.getIn(['ctpMappingWorkflow', 'stagedAssignments'])
const selectCtpModal = (state) =>
  state.getIn(['ctpMappingWorkflow', 'ctpMappingModal'])

export const getCtpMappingModalVisible = createSelector(
  selectCtpModal,
  (ctpModal) => ctpModal.get('visible')
)

export const getSelectedRPs = createSelector(selectSelectedRPs, safeToJS)

export const getCtpAssignments = createSelector(selectCtpAssignments, safeToJS)

export const getCtpStagedAssignments = createSelector(
  selectCtpStagedAssignments,
  safeToJS
)

export const getAllAssignments = createSelector(
  [selectCtpAssignments, selectCtpStagedAssignments],
  (assignments, stagedAssignments) => [
    ...safeToJS(assignments),
    ...safeToJS(stagedAssignments),
  ]
)

const selectAssignmentsMergedWithStage = createSelector(
  [selectCtpAssignments, selectCtpStagedAssignments],
  (assignments, stagedAssignments) => {
    if (assignments.size === 0) return stagedAssignments

    const positions = assignments.map((a) =>
      stagedAssignments.findIndex(
        (s) => s.get('circuitNumber') === a.get('circuitNumber')
      )
    )

    const mergedByPosition = positions.map((position, index) => {
      if (position >= 0) {
        return stagedAssignments.get(position)
      }
      return assignments.get(index)
    })

    const filtered = stagedAssignments.filter(
      (a) =>
        !mergedByPosition.find(
          (m) => m.get('circuitNumber') === a.get('circuitNumber')
        )
    )

    return mergedByPosition.concat(filtered)
  }
)

export const getAssignmentsMergedWithStage = createSelector(
  selectAssignmentsMergedWithStage,
  safeToJS
)

const circuitsToMapSelector = createSelector(
  availableCircuits,
  selectCtpStagedAssignments,
  (circuits, assignments) => {
    return circuits.map((circuit) =>
      // 3 values for mapped. true : already mapped on vehicle || false : needs to be mapped || {pin} : pending assignments is mapping it
      circuit.reduce((acc, curr) => {
        // should only ever be 1 or 0 results
        const stagedAssignments = assignments.filter((assignment) =>
          equals(assignment.get('circuitNumber'), curr.get('circuitNumber'))
        )

        // we want to return the current staged pin object
        if (stagedAssignments.size) {
          return acc.push(curr.set('mapped', get(0, stagedAssignments)))
        }

        // if mapped is already true, we don't need to map it
        if (curr.get('mapped') === false) {
          return acc.push(curr)
        }

        return acc
      }, List())
    )
  }
)

export const getCircuitsByRpToMap = createSelector(
  circuitsToMapSelector,
  safeToJS
)

export const getCircuitsWithLinkedRp = createSelector(
  getCircuitsByRpToMap,
  (circuits) =>
    Object.entries(circuits).reduce((acc, [key, value]) => {
      const c = value.map((v) => ({ ...v, rpUri: key }))
      return [...acc, ...c]
    }, [])
)

export const getTotalUniqueLinkedRps = createSelector(
  circuitsToMapSelector,
  (circuits) => circuits.size
)

const selectCircuitsToMapBydRpUri = createSelector(
  [availableCircuitsByRP, selectCtpStagedAssignments],
  (circuits, assignments) =>
    circuits
      // 3 values for mapped. true : already mapped on vehicle || false : needs to be mapped || {pin} : pending assignments is mapping it
      .reduce((acc, curr) => {
        // should only ever be 1 or 0 results
        const stagedAssignments = assignments.filter((a) =>
          equals(a.get('circuitNumber'), curr.get('circuitNumber'))
        )

        // we want to return the current staged pin object
        if (stagedAssignments.size) {
          return acc.push(curr.set('mapped', get(0, stagedAssignments)))
        }

        // if mapped is already true, we don't need to map it
        if (curr.get('mapped') === false) {
          return acc.push(curr)
        }

        return acc
      }, List())
)

export const getCircuitsToMapBydRpUri = createSelector(
  selectCircuitsToMapBydRpUri,
  safeToJS
)

export const getUnMappedCircuitsToMapBydRpUri = createSelector(
  [getCircuitsToMapBydRpUri, getCtpAssignments, (_, rpUri) => rpUri],
  (circuits, assignments, rpUri) => {
    const updatedCircuits = circuits.map((c) => {
      const match = assignments.find(
        (a) => prop('circuitNumber', c) === prop('circuitNumber', a)
      )
      return {
        ...c,
        mapped: match || false,
      }
    })
    return updatedCircuits.filter(
      (circuit) =>
        isFalse(prop('mapped', circuit)) ||
        equals(path(['mapped', 'linkedRpUri'], circuit), rpUri)
    )
  }
)

const selectStagedRPs = (state) =>
  getInOr(List(), ['addReferenceParametersWorkflow', 'stagedRPs'], state)
const selectAvailableCircuits = (state) =>
  getInOr(List(), ['ctp', 'availableCircuits'], state)

const selectPruneCtpAssignments = createSelector(
  [selectAvailableCircuits, selectStagedRPs, selectCtpAssignments],
  (circuits, stagedRPs, assignments) => {
    const allAvailableCircuits = flattenList(
      stagedRPs.map((rp) => circuits.get(rp.get('uri')))
    ).filter(Boolean)
    const filteredAssignments = assignments.filter((assignment) =>
      allAvailableCircuits.find((circuit) =>
        equals(circuit.get('circuitNumber'), assignment.get('circuitNumber'))
      )
    )
    const updateLinkedRpUri = filteredAssignments.map((assignment) => {
      const matchingRP = stagedRPs.find((rp) =>
        getOr(List(), 'oMaps', rp).find(
          (map) => map.get('physical_slot') === assignment.get('circuitNumber')
        )
      )
      return assignment.set('linkedRpUri', matchingRP.get('uri'))
    })

    return updateLinkedRpUri
  }
)

export const getPruneCtpAssignments = createSelector(
  selectPruneCtpAssignments,
  safeToJS
)
